import React from 'react';
import DataTable from 'react-data-table-component';
import TablePagination from '../reused-components/TablePagination';
import { FEATURE_COLORS } from '../reused-components/Charts/FeatureUsageBarChart';
import { formatCount } from '../../utils/featureUsage';

function UsageBar({ pct, source }) {
    return (
        <span className="tf-usage">
            <span className="tf-usage-track" aria-hidden="true">
                <span className="tf-usage-fill" style={{ width: `${Math.min(100, pct)}%`, background: FEATURE_COLORS[source] }} />
            </span>
            <b>{`${pct}%`}</b>
        </span>
    );
}

function TopValues({ data }) {
    const values = data.top_values || [];
    return (
        <div className="tf-top-values">
            {values.length === 0 && <p>No values reported.</p>}
            {values.map((v) => (
                <div className="tf-top-value" key={v.value}>
                    <span className="tf-value-chip">{v.value}</span>
                    <UsageBar pct={v.pct} source={data.source} />
                    <span className="tf-muted">{`${formatCount(v.sites, data.estimated)} sites`}</span>
                </div>
            ))}
            <p className="tf-muted">
                {data.source === 'pro' ? 'Share of Pro sites.' : 'Share of sites in the current filter.'}
                {data.list && ' Each item in the list is counted separately, so the shares can add up to more than 100%.'}
                {' '}<span className="tf-mono">[custom]</span> means a value was set but its content is not collected; <span className="tf-mono">[redacted]</span> means a secret (key, token, password) is set, and its value is never collected.
            </p>
        </div>
    );
}

const columns = [
    {
        name: 'Rank',
        selector: (row) => row.rank,
        sortable: true,
        width: '110px',
        cell: (row) => <span className="id-span">{row.rank}</span>,
    },
    {
        name: 'Setting',
        selector: (row) => row.label,
        sortable: true,
        grow: 3,
        cell: (row) => (
            <span className="tf-setting">
                <span className="tf-setting-label">
                    {row.label}
                    {row.default_on && <span className="badge tf-badge-default">On by default</span>}
                </span>
                <span className="tf-mono tf-muted">{row.key}</span>
            </span>
        ),
    },
    {
        name: 'Source',
        selector: (row) => row.source,
        sortable: true,
        width: '130px',
        cell: (row) => <span className={`badge tf-badge-${row.source}`}>{row.source === 'pro' ? 'PRO' : 'CORE'}</span>,
    },
    {
        name: 'Sites using',
        selector: (row) => row.sites_using,
        sortable: true,
        style: { justifyContent: 'flex-end' },
        width: '170px',
        cell: (row) => formatCount(row.sites_using, row.estimated),
    },
    {
        name: 'Usage',
        selector: (row) => row.usage_pct,
        sortable: true,
        grow: 2,
        cell: (row) => <UsageBar pct={row.usage_pct} source={row.source} />,
    },
    {
        name: 'Most common value',
        selector: (row) => row.top_values?.[0]?.value ?? '',
        width: '230px',
        cell: (row) => {
            const top = row.top_values?.[0];
            if (!top) return <span className="tf-muted">—</span>;
            return (
                <span>
                    <span className="tf-value-chip">{top.value}</span>{' '}
                    <span className="tf-muted">{`${top.pct}%`}</span>
                </span>
            );
        },
    },
];

export default function FeatureUsageTable({ features, loading }) {
    return (
        <DataTable
            columns={columns}
            data={features}
            keyField="id"
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            paginationComponent={TablePagination}
            expandableRows
            expandableRowsComponent={TopValues}
            expandOnRowClicked
            progressPending={loading}
            progressComponent={(
                <div className="spinner-container">
                    <div className="spinner" />
                </div>
            )}
            noDataComponent={<div className="tf-empty">No settings match your search.</div>}
            highlightOnHover
            pointerOnHover
            responsive
            className="custom-data-table"
        />
    );
}
